import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { BOOKING_LOAD_TIMEOUT_MS, BOOKING_URL, CONTACT_EMAIL } from '@/config/marketing';

/** A message from the HubSpot Meetings page: it posts its height once loaded. */
function fromHubspot(origin: string): boolean {
  try {
    return /(^|\.)hubspot\.com$/.test(new URL(origin).hostname);
  } catch {
    return false;
  }
}

/**
 * The booking widget of the public site: the only booking provider (B3-home).
 *
 * QA-W6-14: a cross-origin iframe gives no load error, and a missing HubSpot
 * page (404) renders inside it. The real page posts its height to the parent
 * once loaded; without any message from hubspot.com within
 * BOOKING_LOAD_TIMEOUT_MS (or on an iframe error), the widget gives way to a
 * contact call to action.
 */
export default function HubspotMeetings({ className = '' }: { className?: string }) {
  const { t } = useTranslation(['admin']);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    if (state !== 'loading') return undefined;
    const onMessage = (e: MessageEvent) => { if (fromHubspot(e.origin)) setState('ready'); };
    window.addEventListener('message', onMessage);
    const timer = window.setTimeout(() => setState((s) => (s === 'loading' ? 'failed' : s)), BOOKING_LOAD_TIMEOUT_MS);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
    };
  }, [state]);

  if (state === 'failed') {
    return (
      <div className={`${className} flex flex-col items-center justify-center gap-4 px-6 py-16 text-center`} data-testid="booking-fallback">
        <p className="text-white/70 text-base max-w-md">{t('admin:landing.contact.bookingUnavailable')}</p>
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-white px-6 text-sm font-medium text-black hover:bg-white/90"
        >
          <Mail className="h-4 w-4" aria-hidden />
          {t('admin:landing.contact.bookingFallbackCta', { email: CONTACT_EMAIL })}
        </a>
      </div>
    );
  }

  return (
    <div className={className} style={{ minHeight: '700px', width: '100%' }}>
      <iframe
        src={BOOKING_URL}
        title={t('admin:landing.contact.bookingTitle')}
        className="w-full"
        style={{ minHeight: '700px', width: '100%', border: '0' }}
        frameBorder="0"
        onError={() => setState('failed')}
      />
    </div>
  );
}
